import React from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, RefreshCw, CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';

interface Verification {
  id: string;
  domain: string;
  status: string;
  check_count: number;
  last_check_at: string | null;
  failure_reason: string | null;
}

interface DomainStep3VerifyProps {
  verification: Verification;
  checking: boolean;
  polling: boolean;
  onCheckNow: () => void;
  onBack: () => void;
}

export function DomainStep3Verify({ 
  verification, 
  checking, 
  polling, 
  onCheckNow, 
  onBack 
}: DomainStep3VerifyProps) {
  const [countdown, setCountdown] = React.useState(10);

  // Countdown timer for polling
  React.useEffect(() => {
    if (!polling) return;

    const timer = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 10 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [polling]);

  const getStatusIcon = () => {
    switch (verification.status) {
      case 'verified':
        return <CheckCircle2 className="h-12 w-12 text-green-600" />;
      case 'failed':
      case 'expired':
        return <XCircle className="h-12 w-12 text-destructive" />;
      default:
        return polling || checking ? (
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
        ) : (
          <Clock className="h-12 w-12 text-amber-600" />
        );
    }
  };

  const getStatusBadge = () => {
    switch (verification.status) {
      case 'verified':
        return <Badge className="bg-green-600">Verified</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center pb-2">
        <h2 className="text-2xl font-semibold mb-2">Verifying Domain</h2>
        <p className="text-muted-foreground">
          We're checking your DNS records for <strong>{verification.domain}</strong>
        </p>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center space-y-4">
            {getStatusIcon()}
            
            <div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <h3 className="text-lg font-medium">{verification.domain}</h3>
                {getStatusBadge()}
              </div>
              
              {verification.status === 'pending' && (
                <p className="text-sm text-muted-foreground">
                  {polling ? (
                    <>Automatically checking in {countdown}s...</>
                  ) : (
                    <>Waiting for DNS records to propagate</>
                  )}
                </p>
              )}
            </div>

            {polling && verification.status === 'pending' && (
              <Progress value={(10 - countdown) * 10} className="w-48 h-2" />
            )}

            {verification.check_count > 0 && (
              <p className="text-xs text-muted-foreground">
                Checked {verification.check_count} time{verification.check_count !== 1 ? 's' : ''}
                {verification.last_check_at && (
                  <> • Last check: {new Date(verification.last_check_at).toLocaleTimeString()}</>
                )}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {verification.failure_reason && verification.status === 'pending' && (
        <Alert variant="destructive" className="bg-destructive/10">
          <XCircle className="h-4 w-4" />
          <AlertTitle>DNS Records Not Found</AlertTitle>
          <AlertDescription className="text-sm">
            {verification.failure_reason}
            <br />
            <span className="text-muted-foreground mt-1 block">
              DNS changes can take up to 48 hours to propagate. Please ensure the records are configured correctly.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {verification.status === 'expired' && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Verification Expired</AlertTitle>
          <AlertDescription>
            The verification window has expired. Please go back and start a new verification.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4">
        <Button variant="outline" onClick={onBack} className="min-h-[44px]">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to DNS
        </Button>
        <Button 
          onClick={onCheckNow} 
          disabled={checking || verification.status === 'verified'}
          className="min-h-[44px]"
        >
          {checking ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Check Now
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
